import api from './api'

export type LeadScope = 'assigned' | 'available' | 'all'

export interface GeoFolder {
    name: string
    slug: string
    sort_order: number
    assigned_count: number
    available_count: number
}

export interface LeadStatusDefinition {
    key: string
    label: string
    substatuses: string[]
    kanban_hidden?: boolean
}

export type LeadTemperatureValue = 'cold' | 'warm' | 'hot'

export interface FirstMessageRuleVariant {
    content: string
    is_formatted: boolean
}

export type FirstMessageRotation = 'round_robin' | 'random'

export interface FirstMessageRule {
    id: number
    name: string
    sort_order: number
    enabled: boolean
    // Тексты правила: их может быть несколько, тогда они чередуются.
    variants: FirstMessageRuleVariant[]
    // По кругу или случайно.
    rotation: FirstMessageRotation
    // Условия правила. Пустой список - условие не задано.
    statuses: string[]
    geos: string[]
    sources: string[]
    temperatures: string[]
    content?: string | null
    is_formatted: boolean
    // Правило без условий - общее, «на всё остальное».
    is_fallback: boolean
    is_filled: boolean
    updated_at?: string | null
}

export interface FirstMessageRulePayload {
    name: string
    enabled: boolean
    variants: FirstMessageRuleVariant[]
    rotation: FirstMessageRotation
    statuses: string[]
    geos: string[]
    sources: string[]
    temperatures: string[]
    content: string
    is_formatted: boolean
}

export interface SourceTemplate {
    source: string
    // Каким правилом покрыт источник и сработает ли оно само по себе.
    rule_name?: string | null
    rule_partial?: boolean
    content?: string | null
    // content - разметка Telegram (жирный, курсив, цитата...).
    is_formatted?: boolean
    is_filled: boolean
    // Участвует ли источник в загрузке лидов.
    source_enabled: boolean
    // Есть ли источник в справочнике CRM: false - его убрали или переименовали.
    is_known: boolean
    updated_at?: string | null
}

// Строка блока «Методы и условия» - в том же виде, в каком её хранит CRM.
export interface LeadMethod {
    method: string
    conditions_deposit: number | null
    conditions_withdrawal: number | null
}

// Списки значений карточки: те же, что в CRM Wiseway.
export interface CrmDictionaries {
    options: Record<string, string[]>
    sources: string[]
    geo_methods: Record<string, string[]>
    method_fallback: string[]
    no_method: string
    synced_fields: string[]
    readonly_fields: string[]
}

export interface LeadChecklistItem {
    id: string
    label: string
    is_checked: boolean
    updated_at?: string | null
    last_user?: string | null
    history?: { action: string; user_name?: string | null; timestamp?: string | null }[]
}

export interface LeadChecklistInfo {
    title: string
    description: string
}

export interface Lead {
    id: number
    title?: string | null
    source?: string | null
    source_details?: string | null
    geo?: string | null
    discovery_source?: string | null
    admin_panel?: string | null
    legacy_conditions?: string | null
    intake_percent?: string | null
    payout_percent?: string | null
    prepay?: string | null
    method?: string | null
    additional_info?: string | null
    first_name?: string | null
    last_name?: string | null
    birth_date?: string | null
    gender?: string | null
    telegram_nickname?: string | null
    telegram_username?: string | null
    email?: string | null
    phone?: string | null
    crm_ww_id?: string | null
    // Поля карточки CRM Wiseway.
    company?: string | null
    lead_type?: string | null
    position?: string | null
    link?: string | null
    key_value?: string | null
    document_type?: string | null
    crm_methods?: LeadMethod[] | null
    telegram_id?: string | null
    desired_terms?: string | null
    desired_amount?: string | null
    disconnection_reason?: string | null
    disconnection_comment?: string | null
    // Маркетинговые метки - приходят из CRM Wiseway, у нас не правятся.
    utm_source?: string | null
    utm_medium?: string | null
    utm_campaign?: string | null
    utm_content?: string | null
    utm_term?: string | null
    landing_page?: string | null
    // Чек-листы АМ - уже сведённые с актуальным набором пунктов.
    checklists?: Record<string, LeadChecklistItem[]>
    checklist_info?: Record<string, LeadChecklistInfo>
    // Чего не хватает для статусов онбординга - по правилам CRM Wiseway.
    onboarding_missing?: string[]
    // Контактные поля вычищены сервером: оператору они не приходят вовсе.
    contacts_hidden?: boolean
    // TG ID скрыт своим переключателем «Доступа операторов».
    telegram_id_hidden?: boolean
    // TG ID в карточке не совпадает с собеседником в диалоге.
    telegram_id_mismatch?: boolean
    // Телеграм в карточке CRM WW не совпадает с нашим - поля из неё не применяются.
    crm_telegram_mismatch?: boolean
    // TG ID по username не нашёлся в Telegram (лид без диалога).
    telegram_id_lookup_problem?: string | null
    // Приходит после отправки, если пул подменил недоступный аккаунт.
    account_switch_notice?: string | null
    temperature?: LeadTemperatureValue | string | null
    dispatch_status?: string
    dispatch_error?: string | null
    dispatch_error_type?: string | null
    dispatch_attempt_at?: string | null
    dispatch_attempts?: number
    dispatch_next_retry_at?: string | null
    work_state?: string
    extra_contacts?: string | null
    onboarding_manager?: string | null
    onboarding_am?: string | null
    offboarding_am?: string | null
    onboarded_at?: string | null
    agent_type?: string | null
    document_1?: string | null
    document_2?: string | null
    kyc_id?: string | null
    kyc_link?: string | null
    chat_link?: string | null
    agent_agreement_link?: string | null
    drop_agreement_link?: string | null
    jira_task_link?: string | null
    am_onboarding_checklist?: string | null
    finalization_checklist?: string | null
    offboarding_checklist?: string | null
    status: string
    sub_status?: string | null
    refusal_reason?: string | null
    manager?: string | null
    contact_email?: string | null
    telegram_avatar_url?: string | null
    assigned_to_id?: number | null
    assigned_to_name?: string | null
    // Профиль видимости - команда, которой принадлежит лид.
    visibility_profile_id?: number | null
    visibility_profile_name?: string | null
    visibility_profile_email?: string | null
    created_by_id?: number | null
    created_by_name?: string | null
    work_item_id?: number | null
    work_status: string
    // Последний прочитанный статус лида в CRM Wiseway.
    crm_status?: string | null
    crm_status_label?: string | null
    crm_status_at?: string | null
    // Статус CRM - фрод: карточка красная везде.
    crm_fraud?: boolean
    // Этим распоряжается их CRM - у нас только для чтения.
    crm_taken_to_work_at?: string | null
    crm_is_duplicate?: boolean | null
    crm_updated_at?: string | null
    // Приходит в ответе на сохранение, если правку не удалось передать в CRM.
    crm_warning?: string | null
    tg_account_id?: number | null
    chat_id?: number | null
    first_message_sent_at?: string | null
    last_message_at?: string | null
    last_error?: string | null
    unread_count?: number
    account_id?: number | null
    account_phone?: string | null
    account_username?: string | null
    account_first_name?: string | null
    account_last_name?: string | null
    account_health_status?: string | null
    account_block_type?: string | null
    account_block_reason?: string | null
    account_blocked_until?: string | null
    created_at: string
    updated_at: string
}

export interface LeadActivity {
    id: number
    event_type: string
    field_name?: string | null
    old_value?: string | null
    new_value?: string | null
    description?: string | null
    operator_id?: number | null
    operator_name?: string | null
    // Почта Wiseway, от которой действовал сотрудник.
    operator_email?: string | null
    created_at: string
}

export interface MessageReaction {
    emoji: string
    count: number
    // Реакция нашего рабочего аккаунта - её и подсвечиваем.
    chosen: boolean
}

export interface AvailableReaction {
    emoji: string
    title: string | null
    premium: boolean
}

// Аккаунт переписки, с которого можно ответить лиду.
// state: ok - пишет как обычно; limited - Telegram может не пропустить;
// unusable - писать с него нельзя (выбрать нельзя).
export interface SenderAccount {
    id: number
    username?: string | null
    phone?: string | null
    label: string
    state: 'ok' | 'limited' | 'unusable'
    reason?: string | null
    is_main: boolean
    last_incoming_at?: string | null
    last_outgoing_at?: string | null
}

export interface SenderAccounts {
    accounts: SenderAccount[]
    main_account_id?: number | null
    last_incoming_account_id?: number | null
    // Основной аккаунт диалога - тёплый или горячий чужого профиля: отвечать с
    // него нельзя, main_account_id тогда - первый разрешённый.
    main_blocked?: boolean
}

export interface LeadMessage {
    id: number
    tg_message_id?: number | null
    text?: string | null
    // Разметка Telegram, если в сообщении есть форматирование.
    text_html?: string | null
    date: string
    outgoing: boolean
    operator_id?: number | null
    operator_name?: string | null
    account_id?: number | null
    account_phone?: string | null
    account_username?: string | null
    // Юзернейм аккаунта на момент сообщения (без @). null - сообщение старше
    // снимков, "" - юзернейма у аккаунта тогда не было.
    message_account_username?: string | null
    account_avatar_url?: string | null
    content_type: string
    reply_to?: number | null
    is_pinned?: boolean
    media_file_name?: string | null
    media_file_path?: string | null
    media_mime_type?: string | null
    media_size?: number | null
    is_read?: boolean
    is_edited?: boolean
    // Лид удалил сообщение у себя - показываем серым с пометкой.
    deleted_by_lead?: boolean
    delivery_status?: 'sent' | 'read' | 'received'
    reactions?: MessageReaction[] | null
}

export interface MessageSearchHit {
    message_id: number
    tg_message_id: number | null
    lead_id: number
    lead_name: string
    chat_id: number
    text: string
    date: string
    outgoing: boolean
}

export interface LeadImportResult {
    total: number
    created: number
    failed: number
    errors: string[]
}

export interface LeadFirstMessageTemplate {
    id: number
    name: string
    content: string
    is_formatted?: boolean
}

export interface LeadSlaRule {
    id: number
    title?: string | null
    status?: string | null
    sub_status?: string | null
    department?: string | null
    responsible_user_id?: number | null
    notify_assignee: boolean
    notify_user_ids: number[]
    time_amount: number
    time_unit: 'minutes' | 'hours' | 'calendar_days' | 'business_days'
    enabled: boolean
    created_at: string
    updated_at: string
}

export type LeadSlaRulePayload = Omit<LeadSlaRule, 'id' | 'created_at' | 'updated_at'>

export const leadsApi = {
    // Справочники для карточки: поля со списком, источники, методы по гео.
    getCrmDictionaries: () => api.get<CrmDictionaries>('/leads/crm-dictionaries'),

    getStatuses: () => api.get<LeadStatusDefinition[]>('/leads/statuses'),

    getFirstMessageTemplate: () => api.get<LeadFirstMessageTemplate>('/leads/first-message-template'),

    updateFirstMessageTemplate: (content: string, isFormatted = false) =>
        api.put<LeadFirstMessageTemplate>('/leads/first-message-template', { content, is_formatted: isFormatted }),

    getSlaRules: () => api.get<LeadSlaRule[]>('/leads/sla-rules'),

    createSlaRule: (data: LeadSlaRulePayload) => api.post<LeadSlaRule>('/leads/sla-rules', data),

    updateSlaRule: (ruleId: number, data: LeadSlaRulePayload) => api.put<LeadSlaRule>(`/leads/sla-rules/${ruleId}`, data),

    deleteSlaRule: (ruleId: number) => api.delete(`/leads/sla-rules/${ruleId}`),

    getGeos: () => api.get<GeoFolder[]>('/leads/geos'),

    getSourceTemplates: () => api.get<SourceTemplate[]>('/leads/source-templates'),

    getFirstMessageRules: () => api.get<FirstMessageRule[]>('/leads/first-message-rules'),

    // Полный справочник статусов CRM Wiseway по группам - для условия «Статус».
    getFirstMessageRuleStatuses: () =>
        api.get<{ groups: Array<{ group: string; statuses: Array<{ code: string; label: string }> }> }>(
            '/leads/first-message-rules/statuses',
        ),

    createFirstMessageRule: (data: FirstMessageRulePayload) =>
        api.post<FirstMessageRule>('/leads/first-message-rules', data),

    updateFirstMessageRule: (ruleId: number, data: FirstMessageRulePayload) =>
        api.put<FirstMessageRule>(`/leads/first-message-rules/${ruleId}`, data),

    deleteFirstMessageRule: (ruleId: number) => api.delete(`/leads/first-message-rules/${ruleId}`),

    reorderFirstMessageRules: (ids: number[]) =>
        api.put<FirstMessageRule[]>('/leads/first-message-rules/order/all', { ids }),

    // Проверка «какое правило сработает» для придуманного лида.
    checkFirstMessageRule: (data: { status?: string | null; geo?: string | null; source?: string | null; temperature?: string | null }) =>
        api.post<{
            rule_id: number | null
            rule_name: string | null
            content: string | null
            is_formatted: boolean
            variants: FirstMessageRuleVariant[]
            rotation: FirstMessageRotation | null
        }>('/leads/first-message-rules/check', data),

    updateSourceTemplate: (source: string, content: string, isFormatted = false) =>
        api.put<SourceTemplate>(`/leads/source-templates/${encodeURIComponent(source)}`, { content, is_formatted: isFormatted }),

    getBroadcastQueue: (params: { dispatch_status: string; search?: string; limit?: number; offset?: number }) =>
        api.get<Lead[]>('/leads/broadcast-queue', { params }),

    getBroadcastQueueCounts: () =>
        api.get<Record<string, number>>('/leads/broadcast-queue/counts'),

    getLeads: (params?: { geo?: string; scope?: LeadScope; status?: string; sub_status?: string; department?: string; assigned_to_id?: number; search?: string; limit?: number; offset?: number }) =>
        api.get<Lead[]>('/leads', { params }),

    getLead: (leadId: number) => api.get<Lead>(`/leads/${leadId}`),

    create: (data: Partial<Lead>) => api.post<Lead>('/leads', data),

    update: (leadId: number, data: Partial<Lead>) => api.put<Lead>(`/leads/${leadId}`, data),

    delete: (leadId: number) => api.delete(`/leads/${leadId}`),

    block: (leadId: number) => api.post<Lead>(`/leads/${leadId}/block`),

    unblock: (leadId: number) => api.post<Lead>(`/leads/${leadId}/unblock`),

    // Убрать разобранную причину неудачной отправки из очереди.
    dismissError: (leadId: number) => api.post<Lead>(`/leads/${leadId}/dismiss-error`),
    cancelDispatch: (leadId: number) => api.post<Lead>(`/leads/${leadId}/cancel-dispatch`),

    importFile: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post<LeadImportResult>('/leads/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    getActivities: (leadId: number) => api.get<LeadActivity[]>(`/leads/${leadId}/activities`),

    setProfile: (leadId: number, visibilityProfileId: number | null) =>
        api.put<Lead>(`/leads/${leadId}/profile`, { visibility_profile_id: visibilityProfileId }),

    assign: (leadId: number, data?: { assigned_to_id?: number; status?: string; sub_status?: string }) =>
        api.post<Lead>(`/leads/${leadId}/assign`, data || {}),

    updateStatus: (leadId: number, data: { status: string; sub_status?: string; assigned_to_id?: number; assign_to_me?: boolean }) =>
        api.patch<Lead>(`/leads/${leadId}/status`, data),

    searchMessages: (query: string, limit = 30, signal?: AbortSignal) =>
        api.get<MessageSearchHit[]>('/leads/search-messages', { params: { q: query, limit }, signal }),

    getMessages: (leadId: number, params?: { limit?: number; offset?: number; before_date?: string; before_id?: number }) =>
        api.get<LeadMessage[]>(`/leads/${leadId}/messages`, { params }),

    // formattedText - тот же текст с разметкой Telegram. Без разметки уходит обычный текст.
    // fromAccountId - с какого аккаунта переписки ответить; пусто - с основного.
    sendMessage: (leadId: number, text: string, forceNewAccount = false, replyToMsgId?: number | null, formattedText?: string | null, fromAccountId?: number | null) =>
        api.post<Lead>(`/leads/${leadId}/send`, {
            text, force_new_account: forceNewAccount, reply_to_msg_id: replyToMsgId, formatted_text: formattedText || null,
            from_account_id: fromAccountId || null,
        }),

    getSenderAccounts: (leadId: number) =>
        api.get<SenderAccounts>(`/leads/${leadId}/sender-accounts`),

    resendFirstMessage: (leadId: number) =>
        api.post<Lead>(`/leads/${leadId}/resend-first-message`),

    sendMedia: (leadId: number, file: File, caption?: string, forceNewAccount = false, replyToMsgId?: number | null, formattedCaption?: string | null, fromAccountId?: number | null) => {
        const formData = new FormData()
        if (fromAccountId) formData.append('from_account_id', String(fromAccountId))
        formData.append('file', file)
        if (caption) formData.append('caption', caption)
        if (formattedCaption) formData.append('formatted_caption', formattedCaption)
        if (forceNewAccount) formData.append('force_new_account', 'true')
        if (replyToMsgId) formData.append('reply_to_msg_id', String(replyToMsgId))
        return api.post<Lead>(`/leads/${leadId}/send-media`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    pinMessage: (leadId: number, messageId: number) =>
        api.post(`/leads/${leadId}/messages/${messageId}/pin`),

    unpinMessage: (leadId: number, messageId: number) =>
        api.post(`/leads/${leadId}/messages/${messageId}/unpin`),

    // Реакции, которые Telegram примет в этом диалоге. Список приходит от него же,
    // поэтому строки эмодзи совпадают побайтово.
    getAvailableReactions: (leadId: number) =>
        api.get<AvailableReaction[]>(`/leads/${leadId}/available-reactions`),

    // Пустой emoji снимает реакцию. Сервер отвечает актуальным снимком.
    reactToMessage: (leadId: number, messageId: number, emoji: string) =>
        api.post<{ status: string; reactions: MessageReaction[] | null; account_avatar_url?: string | null }>(
            `/leads/${leadId}/messages/${messageId}/reaction`, { emoji }
        ),

    editMessage: (leadId: number, messageId: number, text: string, formattedText?: string | null) =>
        api.post(`/leads/${leadId}/messages/${messageId}/edit`, { text, formatted_text: formattedText || null }),

    deleteMessage: (leadId: number, messageId: number, revoke = true) =>
        api.post(`/leads/${leadId}/messages/${messageId}/delete`, null, { params: { revoke } }),

    mediaUrl: (mediaPath: string) => `/api/leads/${mediaPath}?token=${localStorage.getItem('token') || ''}`,

    continueWithAnotherAccount: (leadId: number) =>
        api.post<Lead>(`/leads/${leadId}/continue-account`),

    // Ручной перенос выбранных лидов на сотрудника. assigned_to_id = null
    // снимает ответственного и возвращает лидов в общий пул.
    transferLeads: (leadIds: number[], assignedToId: number | null, comment?: string) =>
        api.post<LeadTransferResult>('/leads/transfer', {
            lead_ids: leadIds,
            assigned_to_id: assignedToId,
            comment: comment || null,
        }),

    // Массовая смена статуса в CRM. Проверки те же, что у одиночной: роль,
    // доступ к лиду и наличие карточки CRM - всё на сервере.
    bulkSetCrmStatus: (leadIds: number[], status: string) =>
        api.post<BulkStatusResult>('/leads/bulk-status', { lead_ids: leadIds, status }),

    // Сколько лидов висит на каждом сотруднике. Только для руководителей.
    getAssigneeSummary: () => api.get<AssigneeSummaryRow[]>('/leads/assignee-summary'),

    // Смена статуса в CRM Wiseway прямо из карточки. Список - только статусы,
    // доступные роли.
    getCrmStatusOptions: (leadId: number) =>
        api.get<{
            groups: { group: string; reasons: { code: string; label: string }[] }[]
            current_status: string | null
            current_label: string | null
            has_crm_card: boolean
        }>(`/leads/${leadId}/crm-status`),

    setCrmStatus: (leadId: number, status: string) =>
        api.post<{ success: boolean; warning: string | null }>(`/leads/${leadId}/crm-status`, { status }),

    // Отметка пункта чек-листа АМ. У лида из CRM сначала пишется в их карточку.
    toggleChecklist: (leadId: number, key: string, itemId: string, checked: boolean) =>
        api.post<Lead>(`/leads/${leadId}/checklist`, { key, item_id: itemId, checked }),
}

export interface LeadTransferResult {
    moved: number
    skipped: number
    assigned_to_id: number | null
    assigned_to_name: string | null
}

export interface BulkStatusResult {
    changed: number
    skipped: number
    failed: number
    status_label: string
    warnings: string[]
}

export interface AssigneeSummaryRow {
    operator_id: number | null
    name: string
    role: string | null
    is_active: boolean
    lead_count: number
    active_count: number
}
